import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { getSystemInfo } from '@renderer/utils/systemInfo'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { ConfirmModal } from '@renderer/components'
import cogoToast from '@successtar/cogo-toast'
import { FaExternalLinkAlt, FaPaste } from 'react-icons/fa'

interface UpgradeModalProps {
  onClose: () => void
}

export const UpgradeModal = ({ onClose }: UpgradeModalProps): React.JSX.Element => {
  const { t } = useTranslation()
  const [activationCode, setActivationCode] = useState('')
  const [processing, setProcessing] = useState(false)
  // Explicit "move licence to this device" confirmation (replaces the old silent rebind).
  const [showMoveConfirm, setShowMoveConfirm] = useState(false)
  const [pendingMoveToken, setPendingMoveToken] = useState('')
  // Use stored deviceId from settingsStore for consistency with validation
  const {
    activationCode: currentActivationCode,
    setActivationCode: storeActivationCode,
    deviceId: storedDeviceId
  } = useSettingsStore()
  const { type: subscriptionType, isValid, computerName, osInfo, endDate, validationError } =
    useSubscriptionStore()

  // Request body shared by /activate and /device. computerName/osInfo describe THIS
  // machine (getSystemInfo), not the previously-bound device.
  const buildPayload = (
    token: string
  ): { token: string; deviceId: string; computerName: string; osInfo: string } => {
    const { computerName: cn, osInfo: oi } = getSystemInfo()
    return { token, deviceId: storedDeviceId, computerName: cn, osInfo: oi }
  }

  const postActivate = async (
    token: string
  ): Promise<{ ok: boolean; data: { error?: string } }> => {
    const response = await fetch(`${process.env.VITE_API_URL}/api/subscription/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(buildPayload(token))
    })
    return { ok: response.ok, data: await response.json() }
  }

  const putDevice = async (
    token: string
  ): Promise<{ ok: boolean; data: { error?: string } }> => {
    const response = await fetch(`${process.env.VITE_API_URL}/api/subscription/device`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(buildPayload(token))
    })
    return { ok: response.ok, data: await response.json() }
  }

  // Re-validate the retained token in place (no server mutation, no reload).
  // Returns whether the subscription is valid afterwards.
  const revalidate = async (): Promise<boolean> => {
    await useCreditsStore.getState().fetchSubscriptionAndCredits()
    return useSubscriptionStore.getState().isValid
  }

  const handlePaste = async (): Promise<void> => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText()
        setActivationCode(text.trim())
        return
      }

      // Fallback for older browsers or when clipboard API is not available
      // Create a temporary textarea element to access clipboard
      const textArea = document.createElement('textarea')
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        const successful = document.execCommand('paste')
        if (successful) {
          setActivationCode(textArea.value.trim())
        } else {
          cogoToast.error(
            t('projectNav.pasteFailed', 'Failed to paste from clipboard. Please paste manually.')
          )
        }
      } catch {
        cogoToast.error(
          t(
            'projectNav.pasteNotSupported',
            'Paste functionality not supported in this browser. Please paste manually.'
          )
        )
      } finally {
        document.body.removeChild(textArea)
      }
    } catch (error) {
      console.error('Paste error:', error)
      cogoToast.error(t('projectNav.pasteError', 'Error accessing clipboard. Please paste manually.'))
    }
  }

  // Activate a freshly-typed token. If the token is already bound to another device,
  // do NOT silently rebind — surface an explicit "move to this device" confirmation.
  const handleActivate = async (): Promise<void> => {
    const token = activationCode.trim()
    if (!token) return
    setProcessing(true)
    try {
      const { ok, data } = await postActivate(token)

      if (!ok && data.error === 'Token has already been used') {
        // Token is bound to another device — require explicit confirmation instead of
        // silently stealing the seat (keeps "1 device = 1 subscription").
        setPendingMoveToken(token)
        setShowMoveConfirm(true)
        return
      }
      if (!ok) {
        throw new Error(
          data.error || t('projectNav.failedActivate', 'Failed to activate subscription')
        )
      }

      storeActivationCode(token)
      cogoToast.success(t('projectNav.activatedSuccess', 'Subscription activated successfully!'))
      window.location.reload() // Refresh the app to update subscription status
    } catch (error) {
      cogoToast.error(
        error instanceof Error
          ? error.message
          : t('projectNav.failedActivate', 'Failed to activate subscription')
      )
    } finally {
      setProcessing(false)
    }
  }

  // Confirmed device move (from either the typed-input flow or one-click recovery).
  const handleConfirmMove = async (): Promise<void> => {
    const token = pendingMoveToken
    setShowMoveConfirm(false)
    if (!token) return
    setProcessing(true)
    try {
      const { ok, data } = await putDevice(token)
      if (!ok) {
        throw new Error(
          data.error || t('projectNav.failedReactivate', 'Failed to reactivate subscription')
        )
      }
      storeActivationCode(token)
      // The event queue is persisted, so this survives the reload below.
      useBoothEventStore.getState().recordLicenseIncident('moved')
      cogoToast.success(t('projectNav.deviceChanged', 'Device changed successfully!'))
      window.location.reload()
    } catch (error) {
      cogoToast.error(
        error instanceof Error
          ? error.message
          : t('projectNav.failedReactivate', 'Failed to reactivate subscription')
      )
    } finally {
      setProcessing(false)
      setPendingMoveToken('')
    }
  }

  // One-click recovery using the ALREADY-STORED token — no copy/paste needed.
  // device_mismatch requires an explicit move confirm; everything else just re-validates.
  const handleRecover = async (): Promise<void> => {
    const token = currentActivationCode
    if (!token) return

    if (validationError === 'device_mismatch') {
      setPendingMoveToken(token)
      setShowMoveConfirm(true)
      return
    }

    setProcessing(true)
    try {
      const ok = await revalidate()
      if (ok) {
        useBoothEventStore.getState().recordLicenseIncident('restored')
        cogoToast.success(t('projectNav.recoverSuccess', 'Subscription restored successfully!'))
        onClose()
      } else {
        cogoToast.error(
          t(
            'projectNav.recoverStillInvalid',
            'Could not restore the subscription. Please try again or contact support.'
          )
        )
      }
    } catch {
      cogoToast.error(
        t(
          'projectNav.recoverStillInvalid',
          'Could not restore the subscription. Please try again or contact support.'
        )
      )
    } finally {
      setProcessing(false)
    }
  }

  // Contextual note for the recovery block, keyed on the validation verdict.
  const recoveryNote = (): string => {
    switch (validationError) {
      case 'device_mismatch':
        return t(
          'projectNav.deactivateWarning',
          'Note: This will deactivate the subscription on your other device.'
        )
      case 'expired':
        return t(
          'projectNav.recoverExpiredNote',
          'Your subscription has expired. Renew it on the dashboard, then check again.'
        )
      case 'not_active':
        return t(
          'projectNav.recoverNotActiveNote',
          'Your subscription is not active yet. Activate it on the dashboard, then check again.'
        )
      case 'session_required':
        return t('projectNav.recoverSessionNote', 'Please sign in again to restore your subscription.')
      case 'wrong_account':
        return t(
          'projectNav.recoverWrongAccountNote',
          'This subscription belongs to a different account. Sign in with the correct account.'
        )
      default:
        return ''
    }
  }

  const recoverLabel =
    validationError === 'device_mismatch'
      ? t('projectNav.recoverMoveDevice', 'Move licence to this computer')
      : validationError === 'expired' || validationError === 'not_active'
        ? t('projectNav.recoverCheckAgain', 'Check again')
        : t('projectNav.recoverReapply', 'Re-apply saved subscription')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[500px]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {t('projectNav.deviceSubscription', 'Device Subscription')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            title={t('common.close', 'Close')}
            aria-label={t('common.close', 'Close')}
          >
            ×
          </button>
        </div>

        {isValid && subscriptionType && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-3 text-blue-600">
              {t('projectNav.activeSubscription', 'Active Subscription')}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('projectNav.planType', 'Plan Type:')}</span>
                <span
                  className={`font-medium bg-clip-text text-transparent ${
                    subscriptionType.toLowerCase() === 'enterprise'
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600'
                      : subscriptionType.toLowerCase() === 'professional'
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                        : subscriptionType.toLowerCase() === 'starter'
                          ? 'bg-gradient-to-r from-green-500 to-teal-500'
                          : 'bg-gradient-to-r from-gray-500 to-gray-600'
                  }`}
                >
                  {subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t('projectNav.computerName', 'Computer Name:')}
                </span>
                <span className="font-medium">{computerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('projectNav.osInfo', 'OS Info:')}</span>
                <span className="font-medium">{osInfo}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t('projectNav.activationCode', 'Activation Code:')}
                </span>
                <span className="font-medium font-mono">
                  {currentActivationCode.substring(0, 10) + '...'}
                </span>
              </div>
              {endDate && (
                <div className="flex justify-between mt-2 pt-2 border-t border-gray-100">
                  <span className="text-gray-600 text-sm">
                    {t('view.subscriptionValidUntil', { date: endDate })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* One-click recovery: re-apply the already-stored token without re-typing it. */}
        {currentActivationCode && !isValid && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold mb-1 text-blue-700">
              {t('projectNav.recoverTitle', 'Restore your subscription')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('projectNav.recoverSavedCode', 'Saved code')}:{' '}
              <span className="font-mono">{currentActivationCode.substring(0, 10) + '...'}</span>
            </p>
            {recoveryNote() && (
              <p className="block mt-2 text-sm text-amber-600">{recoveryNote()}</p>
            )}
            {validationError !== 'wrong_account' && (
              <button
                onClick={handleRecover}
                className="mt-3 w-full px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                disabled={processing}
              >
                {processing ? t('projectNav.recovering', 'Restoring...') : recoverLabel}
              </button>
            )}
            {/* When a token is detected, "Get subscription" is demoted to a secondary action. */}
            <a
              href={`${process.env.VITE_API_URL}/dashboard/subscription`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-full px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              {t('projectNav.getSubscription', 'Get subscription')} <FaExternalLinkAlt />
            </a>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">
            {isValid
              ? t('projectNav.changeSubscription', 'Change Subscription')
              : t('projectNav.activateSubscription', 'Activate Subscription')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('projectNav.enterActivationCode', 'Enter your activation code to {{action}} this device.', {
              action: t('projectNav.activate', 'activate')
            })}
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('projectNav.activationCode', 'Activation Code')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={t(
                  'projectNav.enterActivationCodePlaceholder',
                  'Enter your activation code'
                )}
              />
              <button
                type="button"
                onClick={handlePaste}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-blue-600 transition-colors"
                title={t('projectNav.pasteFromClipboard', 'Paste from clipboard')}
                aria-label={t('projectNav.pasteFromClipboard', 'Paste from clipboard')}
              >
                <FaPaste className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            <p>{t('projectNav.noCodeQuestion', "Don't have an activation code?")}</p>
            <a
              href={`${process.env.VITE_API_URL}/dashboard/subscription`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 font-medium flex items-center gap-2"
            >
              {t('projectNav.purchaseSubscription', 'Purchase a subscription code')}{' '}
              <FaExternalLinkAlt />
            </a>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
            disabled={processing}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleActivate}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
            disabled={processing || !activationCode.trim()}
          >
            {processing
              ? t('projectNav.activating', 'Activating...')
              : t('projectNav.activate', 'Activate')}
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={showMoveConfirm}
        onClose={() => {
          setShowMoveConfirm(false)
          setPendingMoveToken('')
        }}
        onConfirm={handleConfirmMove}
        title={t('projectNav.recoverMoveConfirmTitle', 'Move subscription to this device?')}
        message={t(
          'projectNav.recoverMoveConfirmMessage',
          'This will activate your subscription on THIS computer and deactivate it on the other device. Continue?'
        )}
      />
    </div>
  )
}
